import { createStyles, makeStyles } from "@material-ui/core";
import {
    baseColors,
    CheckCircleIcon,
    CheckCircleOutlineIcon,
    CheckCircleOutlineNegativeIcon,
    currencyWholeDollarFormatter,
    MessageComposerStandardButton,
    RelodeTheme,
    Text,
} from "@relode/core";
import {
    EmploymentGuaranteeType,
    JobPriorityType,
    MarketResearchFeature,
} from "@relode/core/domain";
import * as React from "react";

const useOrderReceiptStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            successFeeDisclaimer: {
                marginTop: 16,
                marginBottom: 16,
                color: baseColors.black,
            },
            receiptSection: {
                display: "flex",
                flexDirection: "column",
            },
            receiptSectionTitle: {
                marginTop: 40,
                marginBottom: 16,
            },
            successFeeOptionCard: {
                border: `1px solid ${baseColors.navy}`,
                backgroundColor: baseColors.cool_blue,
                padding: 16,
                justifyContent: "space-evenly",
            },
            successFeeOptionCardSelected: {
                border: `3px solid ${baseColors.magenta}`,
                backgroundColor: baseColors.light_magenta,
                padding: 18,
                justifyContent: "space-evenly",
            },
            successFeeOptionCardFlex: {
                display: "flex",
                flexDirection: "column",
                alignContent: "space-around",
            },
            successFeeOptionCardRow: {
                display: "flex",
                flexFlow: "row wrap",
                gap: 16,
                margin: theme.spacing(2, 0, 1, 0),
                alignItems: "center",
            },
            successFeeOptionCardTitleRow: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
            },
            successFeeOptionCardTitleRowInner: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
            },
            successFeeOptionCardTitleJobGrade: {
                color: baseColors.navy,
                fontSize: 36,
                fontWeight: 900,
            },
            gradeLabel: {
                color: baseColors.navy,
                alignSelf: "self-end",
            },
            successFeeOptionCardTitleAlt: {
                fontSize: 26,
                fontWeight: 900,
            },
            successFeeOptionCardSuccessFeeTextFlex: {
                margin: theme.spacing(2, 0),
            },
            successFeeOptionCardSuccessFeeLabel: {
                marginTop: theme.spacing(1),
                color: baseColors.navy,
            },
            successFeeTotalLabel: {
                color: baseColors.navy,
            },
            successFeeOptionCardSavingsLabel: {
                backgroundColor: baseColors.light_blue,
                padding: "4px 6px",
                marginTop: theme.spacing(1),
                borderRadius: "3px",
                lineHeight: "100%",
                textAlign: "center",
                textTransform: "uppercase",
                color: baseColors.navy,
                display: "inline-flex",
                fontWeight: 600,
                fontSize: "11.5px",
            },
            successFeeOptionCardPostingFeeAndMarketResearchTextFlex: {
                display: "flex",
                flexDirection: "row",
                gap: 5,
                marginTop: theme.spacing(1),
                textAlign: "center",
            },
            successFeeOptionCardPostingFeeAndMarketResearchText: {
                color: baseColors.navy,
                textAlign: "left",
                fontSize: 14,
            },
        }),
    { name: "OrderReceipt" }
);

type ReceiptSectionProps = {
    title: string;
    children?: React.ReactNode;
};

type SuccessFeeOption = {
    title1: string;
    title2: string;
    subtitle: string;
    successFee: number;
    successFeeSavings: number;
    postingFee: number;
    marketResearchFeatureType: MarketResearchFeature;
    isFullTimeOrPartTime?: boolean;
    isActiveOption?: boolean;
};

export type OrderReceiptProps = {
    planName: string;
    employmentGuarantee: EmploymentGuaranteeType;
    totalPositionQuantity: number;
    jobName: string;
    jobLocation: string;
    jobId: number;
    jobManagerUserId: number;
    jobManagerFullName: string;
    jobManagerRoleName: string;
    className?: string;
    postingFeeInUSDollars: number;
    successFeePercentage: number;
    successFeeInUSDollars: number;
    successFeeSavingsInUSDollars: number;
    defaultPostingFeeInUSDollars: number;
    marketResearchFeatureType: MarketResearchFeature;
    defaultJobPriorityASuccessFeePercentage: number;
    defaultJobPriorityBSuccessFeePercentage: number;
    defaultJobPriorityCSuccessFeePercentage: number;
    defaultJobPriorityDSuccessFeePercentage: number;
    estimatedDefaultJobPriorityASuccessFeeInUSDollars: number;
    estimatedDefaultJobPriorityBSuccessFeeInUSDollars: number;
    estimatedDefaultJobPriorityCSuccessFeeInUSDollars: number;
    estimatedDefaultJobPriorityDSuccessFeeInUSDollars: number;
    estimatedDefaultJobPriorityASuccessFeeSavingsInUSDollars: number;
    estimatedDefaultJobPriorityBSuccessFeeSavingsInUSDollars: number;
    estimatedDefaultJobPriorityCSuccessFeeSavingsInUSDollars: number;
    estimatedDefaultJobPriorityDSuccessFeeSavingsInUSDollars: number;
    isFullTimeOrPartTime: boolean;
    jobPriorityType: JobPriorityType;
    billRateInUSDollars: number;
    estimatedContractBillTotalInUSDollars: number;
    estimatedContractBillTotalSavingsInUSDollars: number;
    hasSignedAgreement: boolean;
    payRateInUSDollars: number;
    contractStartDate: string;
    contractEndDate: string;
    hoursPerWeek: number;
};

export const OrderReceipt = (props: OrderReceiptProps) => {
    const {
        planName,
        employmentGuarantee,
        className,
        jobId,
        jobName,
        jobLocation,
        jobManagerUserId,
        jobManagerFullName,
        jobManagerRoleName,
        isFullTimeOrPartTime,
        payRateInUSDollars,
        contractStartDate,
        contractEndDate,
        hoursPerWeek,
    } = props;

    const classes = useOrderReceiptStyles(props);

    const successFeeOptions = GenerateSuccessFeeOptions(props);

    if (!(successFeeOptions?.length > 0)) return null;

    return (
        <div className={!!className ? className : ""}>
            {!!planName ? (
                <Text component="span">
                    You are currently on our <b>{planName}</b> plan.
                </Text>
            ) : null}
            {successFeeOptions?.length > 0 ? (
                <>
                    <Text variant="h4" className={classes.receiptSectionTitle}>
                        order estimate (per hire)
                    </Text>
                    <div className={classes.successFeeOptionCardRow}>
                        {successFeeOptions.map((sfo, i) => (
                            <SuccessFeeOptionCard
                                key={i}
                                title1={sfo.title1}
                                title2={sfo.title2}
                                subtitle={sfo.subtitle}
                                successFee={sfo.successFee}
                                successFeeSavings={sfo.successFeeSavings}
                                postingFee={sfo.postingFee}
                                marketResearchFeatureType={
                                    sfo.marketResearchFeatureType
                                }
                                isActiveOption={sfo.isActiveOption}
                                isFullTimeOrPartTime={sfo.isFullTimeOrPartTime}
                            />
                        ))}
                    </div>
                    <Text
                        variant="caption"
                        className={classes.successFeeDisclaimer}
                    >
                        {isFullTimeOrPartTime
                            ? `* Success fee calculation is based on job's given salary
                            and job grade's success fee percentage.`
                            : `* Calculation is based on a $${payRateInUSDollars}/hr pay rate
                            plus negotiated markup, working ${hoursPerWeek} hrs/week,
                            for the duration of this contract (${contractStartDate} - ${contractEndDate})`}
                    </Text>
                </>
            ) : null}
            <Text variant="h4" className={classes.receiptSectionTitle}>
                Looking for additional savings?
            </Text>
            <MessageComposerStandardButton
                title="request a quote"
                jobName={jobName}
                jobId={jobId}
                initialRecipients={[
                    {
                        userId: jobManagerUserId,
                        displayName: jobManagerFullName,
                        roleName: jobManagerRoleName,
                    },
                ]}
                initialBodyText={
                    "I would like to learn more about available plan options " +
                    "and ways to get additional savings on the following job:\n\n" +
                    `Job: ${jobName}\n` +
                    `Location: ${jobLocation}\n\n`
                }
            />
            <ReceiptSection title="payment terms">
                <Text>
                    {isFullTimeOrPartTime
                        ? `Relode will invoice applicable job posting fees upon job
                        order. Success fees for hired candidates will be invoiced on
                        candidate's actual first day of employment. All payments are
                        due 15 business days from receipt of invoice.`
                        : `Relode will invoice client bi-weekly based on the number of hours
                        recorded in timesheets and the start date of the engagement for hired candidates.
                        All payments are due net 15 from the date of invoice.`}
                </Text>
            </ReceiptSection>
            {isFullTimeOrPartTime && employmentGuarantee?.description ? (
                <ReceiptSection title="employment guarantee">
                    <Text>{employmentGuarantee.description}</Text>
                </ReceiptSection>
            ) : null}
        </div>
    );
};

const GenerateSuccessFeeOptions = (props: OrderReceiptProps) => {
    const {
        postingFeeInUSDollars,
        successFeePercentage,
        successFeeInUSDollars,
        successFeeSavingsInUSDollars,
        defaultPostingFeeInUSDollars,
        marketResearchFeatureType,
        defaultJobPriorityASuccessFeePercentage,
        defaultJobPriorityBSuccessFeePercentage,
        defaultJobPriorityCSuccessFeePercentage,
        defaultJobPriorityDSuccessFeePercentage,
        estimatedDefaultJobPriorityASuccessFeeInUSDollars,
        estimatedDefaultJobPriorityBSuccessFeeInUSDollars,
        estimatedDefaultJobPriorityCSuccessFeeInUSDollars,
        estimatedDefaultJobPriorityDSuccessFeeInUSDollars,
        estimatedDefaultJobPriorityASuccessFeeSavingsInUSDollars,
        estimatedDefaultJobPriorityBSuccessFeeSavingsInUSDollars,
        estimatedDefaultJobPriorityCSuccessFeeSavingsInUSDollars,
        estimatedDefaultJobPriorityDSuccessFeeSavingsInUSDollars,
        isFullTimeOrPartTime,
        jobPriorityType,
        hasSignedAgreement,
        billRateInUSDollars,
        estimatedContractBillTotalInUSDollars,
        estimatedContractBillTotalSavingsInUSDollars,
    } = props;

    let successFeeOptions: SuccessFeeOption[];

    if (
        typeof defaultJobPriorityASuccessFeePercentage === "undefined" ||
        typeof defaultJobPriorityBSuccessFeePercentage === "undefined" ||
        typeof defaultJobPriorityCSuccessFeePercentage === "undefined" ||
        typeof defaultJobPriorityDSuccessFeePercentage === "undefined" ||
        typeof billRateInUSDollars === "undefined" ||
        typeof estimatedContractBillTotalInUSDollars === "undefined" ||
        typeof estimatedContractBillTotalSavingsInUSDollars === "undefined"
    )
        return successFeeOptions;

    if (isFullTimeOrPartTime) {
        const isSuccessFeeUniversal = JobPriorityType.isSuccessFeeUniversal(
            defaultJobPriorityASuccessFeePercentage,
            defaultJobPriorityBSuccessFeePercentage,
            defaultJobPriorityCSuccessFeePercentage,
            defaultJobPriorityDSuccessFeePercentage
        );
        if (
            isSuccessFeeUniversal ||
            defaultJobPriorityASuccessFeePercentage === null
        ) {
            if (
                hasSignedAgreement &&
                successFeePercentage !== null &&
                successFeeInUSDollars !== null
            ) {
                successFeeOptions = [
                    {
                        title1: jobPriorityType?.grade ?? "A-D",
                        title2: "grade",
                        subtitle: `${successFeePercentage}% success fee`,
                        successFee: successFeeInUSDollars,
                        successFeeSavings: successFeeSavingsInUSDollars,
                        postingFee: postingFeeInUSDollars,
                        marketResearchFeatureType: marketResearchFeatureType,
                        isActiveOption: typeof jobPriorityType !== "undefined",
                    },
                ];
            } else if (
                typeof defaultJobPriorityASuccessFeePercentage === "number"
            ) {
                successFeeOptions = [
                    {
                        title1: "A-D",
                        title2: "grade",
                        subtitle: `${defaultJobPriorityASuccessFeePercentage}% success fee`,
                        successFee: estimatedDefaultJobPriorityASuccessFeeInUSDollars,
                        successFeeSavings: estimatedDefaultJobPriorityASuccessFeeSavingsInUSDollars,
                        postingFee: defaultPostingFeeInUSDollars,
                        marketResearchFeatureType: marketResearchFeatureType,
                    },
                ];
            }
        } else {
            const isPriorityTypeA = jobPriorityType === JobPriorityType.A;
            const isPriorityTypeB = jobPriorityType === JobPriorityType.B;
            const isPriorityTypeC = jobPriorityType === JobPriorityType.C;
            const isPriorityTypeD = jobPriorityType === JobPriorityType.D;

            successFeeOptions = [
                {
                    title1: "A",
                    title2: "grade",
                    subtitle: `${
                        hasSignedAgreement && isPriorityTypeA
                            ? successFeePercentage
                            : defaultJobPriorityASuccessFeePercentage
                    }% success fee`,
                    successFee:
                        hasSignedAgreement && isPriorityTypeA
                            ? successFeeInUSDollars
                            : estimatedDefaultJobPriorityASuccessFeeInUSDollars,
                    successFeeSavings:
                        hasSignedAgreement && isPriorityTypeA
                            ? successFeeSavingsInUSDollars
                            : estimatedDefaultJobPriorityASuccessFeeSavingsInUSDollars,
                    postingFee:
                        hasSignedAgreement && isPriorityTypeA
                            ? postingFeeInUSDollars
                            : defaultPostingFeeInUSDollars,
                    marketResearchFeatureType: marketResearchFeatureType,
                    isActiveOption: isPriorityTypeA,
                },
                {
                    title1: "B",
                    title2: "grade",
                    subtitle: `${
                        hasSignedAgreement && isPriorityTypeB
                            ? successFeePercentage
                            : defaultJobPriorityBSuccessFeePercentage
                    }% success fee`,
                    successFee:
                        hasSignedAgreement && isPriorityTypeB
                            ? successFeeInUSDollars
                            : estimatedDefaultJobPriorityBSuccessFeeInUSDollars,
                    successFeeSavings:
                        hasSignedAgreement && isPriorityTypeB
                            ? successFeeSavingsInUSDollars
                            : estimatedDefaultJobPriorityBSuccessFeeSavingsInUSDollars,
                    postingFee:
                        hasSignedAgreement && isPriorityTypeB
                            ? postingFeeInUSDollars
                            : defaultPostingFeeInUSDollars,
                    marketResearchFeatureType: marketResearchFeatureType,
                    isActiveOption: isPriorityTypeB,
                },
                {
                    title1: "C",
                    title2: "grade",
                    subtitle: `${
                        hasSignedAgreement && isPriorityTypeC
                            ? successFeePercentage
                            : defaultJobPriorityCSuccessFeePercentage
                    }% success fee`,
                    successFee:
                        hasSignedAgreement && isPriorityTypeC
                            ? successFeeInUSDollars
                            : estimatedDefaultJobPriorityCSuccessFeeInUSDollars,
                    successFeeSavings:
                        hasSignedAgreement && isPriorityTypeC
                            ? successFeeSavingsInUSDollars
                            : estimatedDefaultJobPriorityCSuccessFeeSavingsInUSDollars,
                    postingFee:
                        hasSignedAgreement && isPriorityTypeC
                            ? postingFeeInUSDollars
                            : defaultPostingFeeInUSDollars,
                    marketResearchFeatureType: marketResearchFeatureType,
                    isActiveOption: isPriorityTypeC,
                },
                {
                    title1: "D",
                    title2: "grade",
                    subtitle: `${
                        hasSignedAgreement && isPriorityTypeD
                            ? successFeePercentage
                            : defaultJobPriorityDSuccessFeePercentage
                    }% success fee`,
                    successFee:
                        hasSignedAgreement && isPriorityTypeD
                            ? successFeeInUSDollars
                            : estimatedDefaultJobPriorityDSuccessFeeInUSDollars,
                    successFeeSavings:
                        hasSignedAgreement && isPriorityTypeD
                            ? successFeeSavingsInUSDollars
                            : estimatedDefaultJobPriorityDSuccessFeeSavingsInUSDollars,
                    postingFee:
                        hasSignedAgreement && isPriorityTypeD
                            ? postingFeeInUSDollars
                            : defaultPostingFeeInUSDollars,
                    marketResearchFeatureType: marketResearchFeatureType,
                    isActiveOption: isPriorityTypeD,
                },
            ];
        }
    } else {
        if (
            billRateInUSDollars > 0 &&
            estimatedContractBillTotalInUSDollars > 0
        ) {
            successFeeOptions = [
                {
                    title1: "CONTRACT",
                    title2: null,
                    subtitle: `Billed at $${billRateInUSDollars}/hr`,
                    successFee: estimatedContractBillTotalInUSDollars,
                    successFeeSavings: estimatedContractBillTotalSavingsInUSDollars,
                    postingFee:
                        postingFeeInUSDollars ?? defaultPostingFeeInUSDollars,
                    marketResearchFeatureType: marketResearchFeatureType,
                    isFullTimeOrPartTime: isFullTimeOrPartTime,
                    isActiveOption:
                        jobPriorityType === JobPriorityType.A ||
                        jobPriorityType === JobPriorityType.B ||
                        jobPriorityType === JobPriorityType.C ||
                        jobPriorityType === JobPriorityType.D,
                },
            ];
        }
    }
    return successFeeOptions;
};

const SuccessFeeOptionCard = (props: SuccessFeeOption) => {
    const {
        title1,
        title2,
        subtitle,
        successFee,
        successFeeSavings,
        postingFee,
        marketResearchFeatureType,
        isFullTimeOrPartTime = true,
        isActiveOption = false,
    } = props;

    const classes = useOrderReceiptStyles(props);

    const areMarketMarketResearchEnabled =
        marketResearchFeatureType === MarketResearchFeature.Anytime ||
        marketResearchFeatureType === MarketResearchFeature.AfterSigned;

    return (
        <>
            <div
                className={
                    isActiveOption
                        ? classes.successFeeOptionCardSelected
                        : classes.successFeeOptionCard
                }
            >
                <div className={classes.successFeeOptionCardFlex}>
                    <div>
                        <div className={classes.successFeeOptionCardTitleRow}>
                            <div
                                className={
                                    classes.successFeeOptionCardTitleRowInner
                                }
                            >
                                <Text
                                    variant="h2"
                                    className={
                                        isFullTimeOrPartTime
                                            ? classes.successFeeOptionCardTitleJobGrade
                                            : classes.successFeeOptionCardTitleAlt
                                    }
                                >
                                    {title1}
                                </Text>
                                <Text
                                    variant="body1"
                                    className={classes.gradeLabel}
                                >
                                    {title2}
                                </Text>
                            </div>
                            {isActiveOption ? (
                                <CheckCircleIcon
                                    htmlColor={baseColors.magenta}
                                />
                            ) : null}
                        </div>
                        <Text
                            variant="body1"
                            className={
                                classes.successFeeOptionCardSuccessFeeLabel
                            }
                        >
                            {subtitle}
                        </Text>
                    </div>
                    <div
                        className={
                            classes.successFeeOptionCardSuccessFeeTextFlex
                        }
                    >
                        <Text
                            variant="h2"
                            className={classes.successFeeTotalLabel}
                        >
                            {`${currencyWholeDollarFormatter.format(
                                successFee
                            )} *`}
                        </Text>
                        {successFeeSavings > 0 ? (
                            <Text
                                variant="h2"
                                className={
                                    classes.successFeeOptionCardSavingsLabel
                                }
                            >
                                SAVE{" "}
                                {currencyWholeDollarFormatter.format(
                                    successFeeSavings
                                )}
                            </Text>
                        ) : null}
                    </div>
                    <div>
                        <div
                            className={
                                classes.successFeeOptionCardPostingFeeAndMarketResearchTextFlex
                            }
                        >
                            <CheckCircleOutlineIcon color="primary" />
                            <Text
                                variant="h6"
                                className={
                                    classes.successFeeOptionCardPostingFeeAndMarketResearchText
                                }
                            >
                                {currencyWholeDollarFormatter.format(
                                    postingFee
                                )}{" "}
                                job posting fee
                            </Text>
                        </div>
                        <div
                            className={
                                classes.successFeeOptionCardPostingFeeAndMarketResearchTextFlex
                            }
                        >
                            {areMarketMarketResearchEnabled ? (
                                <CheckCircleOutlineIcon color="primary" />
                            ) : (
                                <CheckCircleOutlineNegativeIcon />
                            )}
                            <Text
                                variant="h6"
                                className={
                                    classes.successFeeOptionCardPostingFeeAndMarketResearchText
                                }
                            >
                                Access to market research
                            </Text>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const ReceiptSection = (props: ReceiptSectionProps) => {
    const { title, children } = props;
    const classes = useOrderReceiptStyles(props);
    return (
        <div className={classes.receiptSection}>
            <Text variant="h4" className={classes.receiptSectionTitle}>
                {title}
            </Text>
            {children}
        </div>
    );
};
